import React, { useMemo } from 'react';
import * as yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useAuth } from '../../auth/context/AuthContext';
import StorageService from '../../utilities/storageService';
import { MAIN_ACCOUNT_INFO_KEY } from '../../firebase/constants';
import { useFormErrors } from '../../components/stepper/useStepperForm';
import {
  FormInput,
  FormSelect,
} from '../../components/stepper/field-components';
import ControlledCheckbox from '../../components/stepper/field-components/FormCheckboxGroup';
import moment from 'moment';
import { FULL_DATE, STANDARD_DATE } from '../../utilities/dateHelper';
import ButtonComponent from '../../components/button/buttonComponent';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Hasura_Legal_Document_Types_Enum,
  Persona_Inquiry_Status_Enum,
  useAcceptLegalDocumentMutation,
  useAcceptUserAgreementMutation,
  useGetAgreementSudRecordsByUserIdQuery,
  UserAgreementAcceptorRole,
  UserAgreementTypes,
} from 'graphql/generated/hasura';
import { Roles } from '../../firebase/interfaces';
import Loader from 'components/loaderComponent';
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router-dom';
import { useCustomToast } from '../../layout/useToast';
import { MY_ACCOUNT_PROFILE } from '../../utilities/routes';
import { usePatientProfileContext } from '../../app/my-account/pages/patient/PatientProfileContext';
import { useGetComponent } from '../../hooks/useGetComponent';
import { componentIds } from '../../utilities/constants';

const mainAccountInfoStorage = new StorageService(MAIN_ACCOUNT_INFO_KEY);

enum UserAgreementAcceptorRoleHasura {
  Parent = 1,
  Guardian = 2,
  OtherAuthorizedAdult = 3,
  Patient = 4,
}

const NoticeOfPrivacyPracticesSudRecords = () => {
  const { user, selectedAccount } = useAuth();
  const { updatePatientValidation } = usePatientProfileContext();
  const { addToast } = useCustomToast();
  const navigate = useNavigate();
  const mainAccountInfo = mainAccountInfoStorage.getData();

  const { data: locale, loading } = useGetComponent({
    componentId: componentIds.NOTICE_Of_PRIVACY_SUD,
    locale: 'en',
  });

  const schemas = yup.object().shape({
    name: yup.string().required('Required field'),
    personCompleting: yup.string().required('Required field'),
    role: yup.string().required('Required field'),

    hasAccessToSUDRecordsNotice: yup.boolean().oneOf([true], 'Required field'),
    acknowledgementIsNotConsentToDisclose: yup
      .boolean()
      .oneOf([true], 'Required field'),
    isMinorAccessLimitedByPrivacyLaws: yup
      .boolean()
      .oneOf([true], 'Required field'),
    agreesToSUDRecordsPrivacyPractices: yup
      .boolean()
      .oneOf([true], 'Required field'),
  });

  const [acceptUserAgreement] = useAcceptUserAgreementMutation({
    refetchQueries: ['GetAgreementsByUserId', 'GetAgreementSudRecordsByUserId'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      addToast({
        type: 'success',
        message:
          'Notice of Privacy Practices for SUD Record successfully signed',
      });
      updatePatientValidation();
      navigate(MY_ACCOUNT_PROFILE);
    },
  });

  const { onError } = useFormErrors();
  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schemas),
    values: {
      name: user?.displayName ?? '',
      personCompleting:
        (mainAccountInfo?.name || user?.displayName) ?? undefined,
      role:
        (selectedAccount
          ? UserAgreementAcceptorRole.Parent
          : UserAgreementAcceptorRole.Patient) ?? '',

      hasAccessToSUDRecordsNotice: false,
      acknowledgementIsNotConsentToDisclose: false,
      isMinorAccessLimitedByPrivacyLaws: false,
      agreesToSUDRecordsPrivacyPractices: false,
    },
  });

  const { data: userAgreements, loading: loadingAgreement } =
    useGetAgreementSudRecordsByUserIdQuery({
      fetchPolicy: 'network-only',
      skip: user?.role !== Roles.PATIENT,
      variables: { id: selectedAccount || user?.uuid || '' },
      onCompleted: (data) => {
        const isVerified =
          data?.user_agreements?.[0]?.status ===
          Persona_Inquiry_Status_Enum.Approved;
        if (isVerified) {
          methods.setValue('hasAccessToSUDRecordsNotice', true);
          methods.setValue('acknowledgementIsNotConsentToDisclose', true);
          methods.setValue('isMinorAccessLimitedByPrivacyLaws', true);
          methods.setValue('agreesToSUDRecordsPrivacyPractices', true);
        }
      },
    });

  const isVerified = useMemo(
    () =>
      userAgreements?.user_agreements?.[0]?.status ===
      Persona_Inquiry_Status_Enum.Approved,
    [userAgreements],
  );

  const patientRoles = useMemo(
    () => [
      {
        value: UserAgreementAcceptorRole.Patient,
        label: UserAgreementAcceptorRole.Patient,
      },
    ],
    [],
  );

  const childRolesLabels = useMemo(
    () => ({
      [UserAgreementAcceptorRole.Patient]: 'Patient',
      [UserAgreementAcceptorRole.Parent]: 'Parent',
      [UserAgreementAcceptorRole.Guardian]: 'Guardian',
      [UserAgreementAcceptorRole.OtherAuthorizedAdult]:
        'Other authorized adult',
    }),
    [],
  );

  const rolesLabels = useMemo(
    () => ({
      [UserAgreementAcceptorRoleHasura.Patient]: 'Patient',
      [UserAgreementAcceptorRoleHasura.Parent]: 'Parent',
      [UserAgreementAcceptorRoleHasura.Guardian]: 'Guardian',
      [UserAgreementAcceptorRoleHasura.OtherAuthorizedAdult]:
        'Other authorized adult',
    }),
    [],
  );

  const childRoles = useMemo(
    () =>
      [
        UserAgreementAcceptorRole.Parent,
        UserAgreementAcceptorRole.Guardian,
        UserAgreementAcceptorRole.OtherAuthorizedAdult,
      ].map((v) => ({
        value: v,
        label: childRolesLabels?.[v],
      })),
    [childRolesLabels],
  );

  const [acceptLegalDocument] = useAcceptLegalDocumentMutation({
    variables: {
      legal_document_type:
        Hasura_Legal_Document_Types_Enum.ConsentToUseAndDiscloseSudRecords,
    },
    refetchQueries: ['GetMyAcceptedLegalDocuments'],
  });

  const onSubmit = (data: yup.InferType<typeof schemas>) => {
    return Promise.all([
      acceptLegalDocument(),
      acceptUserAgreement({
        variables: {
          agreementType: UserAgreementTypes.NoticeOfPrivacySudRecords,
          acceptorRole: data.role as UserAgreementAcceptorRole,
        },
      }),
    ]);
  };

  if (loadingAgreement || loading) {
    return <Loader />;
  }

  return (
    <div className="flex-wrap px-2.5 desktop:px-30">
      <h2 className="desktop:text-h1 text-h2 text-neutral-800 flex font-medium mb-30">
        {locale?.title}
        <span className="text-alert-negative text-sm font-bold ml-auto">
          {locale?.required}
        </span>
      </h2>
      <div className="font-medium text-med-gray-3 flex flex-col gap-30">
        <p className={twMerge('w-full', isVerified && 'desktop:max-w-[52vw]')}>
          {locale?.paragraph1}
        </p>

        <p>
          {locale?.paragraph2}{' '}
          <a
            href="mailto:privacy@decode-me.com"
            className="underline text-clc-blue ml-1"
          >
            privacy@decode-me.com
          </a>
          .
        </p>
      </div>

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit, onError)}
          className="flex flex-col mt-30 gap-30"
          noValidate
        >
          <div
            className={twMerge(
              'flex items-center gap-2.5',
              isVerified && 'hidden',
            )}
          >
            <FormInput
              disabled={!!user?.displayName}
              name="name"
              label={locale?.name}
              maxLength={100}
              isRequired
            />
            <FormInput
              name="personCompleting"
              label={locale?.personCompleting}
              disabled={!!mainAccountInfo?.name}
              maxLength={100}
              isRequired
            />
            <FormSelect
              name="role"
              label={locale?.role}
              disabled={!selectedAccount}
              options={selectedAccount ? childRoles : patientRoles}
              isRequired
            />
          </div>

          <div className="flex flex-col gap-2.5">
            <ControlledCheckbox
              disabled={isVerified}
              name="hasAccessToSUDRecordsNotice"
              isAlternativeColor
              label={locale?.hasAccessToSUDRecordsNotice}
            />
            <ControlledCheckbox
              disabled={isVerified}
              isAlternativeColor
              name="acknowledgementIsNotConsentToDisclose"
              label={locale?.acknowledgementIsNotConsentToDisclose}
            />
            <ControlledCheckbox
              disabled={isVerified}
              isAlternativeColor
              name="isMinorAccessLimitedByPrivacyLaws"
              label={locale?.isMinorAccessLimitedByPrivacyLaws}
            />
            {!isVerified && (
              <ControlledCheckbox
                isAlternativeColor
                name="agreesToSUDRecordsPrivacyPractices"
                label={locale?.agreesToSUDRecordsPrivacyPractices}
              />
            )}
          </div>

          <p
            className={twMerge(
              'font-medium text-med-gray-3',
              isVerified && 'hidden',
            )}
          >
            {locale?.date} {moment().format(STANDARD_DATE)}
          </p>

          <ButtonComponent
            disabled={
              methods.formState.isSubmitting || !methods.formState.isValid
            }
            showSpinner={methods.formState.isSubmitting}
            className={twMerge('!w-fit', isVerified && 'hidden')}
            htmlButtonType="submit"
            testID="accept"
          >
            {locale?.accept}
          </ButtonComponent>
        </form>
      </FormProvider>
      <div
        className={twMerge(
          'text-med-gray-3 font-medium mt-30',
          !isVerified && 'hidden',
        )}
      >
        <p>
          {locale?.name}: {user?.displayName}{' '}
        </p>
        <p>
          {locale?.personCompletingLabel}: {mainAccountInfo?.name}
        </p>
        <p>
          {locale?.role}:{' '}
          {
            rolesLabels[
              userAgreements?.user_agreements?.[0]
                ?.acceptor_role as UserAgreementAcceptorRoleHasura
            ]
          }
        </p>
        <p>
          {locale?.dateTime}:{' '}
          {moment(userAgreements?.user_agreements?.[0]?.created_at).format(
            FULL_DATE,
          )}
        </p>
        <p>
          {locale?.signature}: {userAgreements?.user_agreements?.[0]?.id}
        </p>
      </div>
    </div>
  );
};

export default NoticeOfPrivacyPracticesSudRecords;
